import React from 'react';
import { FieldValues, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import {
  ErrorField,
  InputText,
  Textarea,
  InputColor,
  TextEditor,
  Checkbox,
  AsyncStyledSelect,
  StyledSelect,
} from '../../lib/HooksFormFields';
import ChildPagesField from '../ChildPagesField';
import ObjectArray from '../ObjectArray';
import InputDateTime from '../../lib/HooksFormFields/InputDateTime';
import InputMedia from '../../lib/HooksFormFields/InputMedia';
import { IPage, PageField } from '../../types/page';

import styles from './Field.module.scss';
import FieldSchedule from '../FieldSchedule';
import FieldSlider from '../FieldSlider';
import ListTags from '../../lib/HooksFormFields/ListTags';
import { get } from '../../utils';
// import Checkbox from '../../lib/HooksFormFields/Checkbox';

const Field = ({
  field,
  defaultValue,
  control,
  errors,
  page = null,
  setValue,
  watch,
  pageId,
  setIsSaved,
  submit,
}:{
  field: PageField,
  defaultValue?: any,
  control: any,
  errors: any,
  pageId: string | null
  page: IPage | null | any
  watch?: UseFormWatch<FieldValues>,
  setValue: UseFormSetValue<FieldValues>,
  setIsSaved?: (bool: boolean) => void,
  submit: (data: any) => void,
}) => {
  const { error } = useSelector((store: any) => store).pagesReducer;

  const rules = field.required ? { required: 'Ce champs est requis' } : {};

  function getError() {
    let message = errors && field.name && errors[field.name]?.message;
    const serverDuplicated = error === `La valeur du ou des champs suivant est déjà utilisé : ${field.name}.` && 'La valeur est déja utilisé dans une autre page';
    const keys = field?.name?.split('.');
    let obj = errors;
    keys.forEach((k) => {
      if (obj[k]) {
        obj = obj[k];
        if (obj.message) {
          message = obj.message;
        }
      }
    });
    if (!message && serverDuplicated) {
      message = serverDuplicated;
    }
    return message;
  }

  async function handleSubmit(obj: any) {
    return submit(obj);
  }

  return (
    <div className={`${styles.field}`}>
      {field.title && (
        <h2>{field.title}</h2>
      )}
      {field.type === 'text'
        && <>
          <InputText
            label={field.label}
            placeholder={field.placeholder || ''}
            name={field.name}
            control={control}
            rules={rules}
            heading={field.heading}
          />
        </>
      }
      {field.type === 'color' && (
        <InputColor
          label={field.label}
          name={field.name}
          control={control}
          rules={rules}
        />
      )}
      {field.type === 'checkbox' && (
        <Checkbox
          label={field.label}
          name={field.name}
          control={control}
        />
      )}
      {field.type === 'richtext'
        && <>
          <TextEditor
            label={field.label}
            placeholder={field.placeholder || ''}
            name={field.name}
            control={control}
            rules={rules}
            maxlength={field.maxlength}
            defaultValue={defaultValue}
          />
        </>
      }
      {field.type === 'textarea' && (
        <Textarea
          label={field.label}
          placeholder={field.placeholder || ''}
          name={field.name}
          maxlength={field.maxlength}
          control={control}
          rules={rules}
        />
      )}
      {field.type === 'media' && pageId && (
        <InputMedia
          label={field.label}
          name={field.name}
          media={get(page, field.name)}
          handleChange={(val) => setValue(field.name, val)}
          submit={(obj) => handleSubmit(obj)}
        />
      )}

      {field.type === 'date' && (
        <InputDateTime
          label={field.label}
          name={field.name}
          control={control}
          rules={rules}
        />
      )}
      {(field.type === 'selectAsync' && field.endpoint) && (
        <AsyncStyledSelect
          label={field.label}
          endpoint={field.endpoint}
          name={field.name}
          isMulti={field.isMulti}
          control={control}
          rules={rules}
        />
      )}
      {field.type === 'schedule' && (
        <FieldSchedule
          name={field.name}
          control={control}
          rules={rules}
        />
      )}
      {field.type === 'slider' && (
        <FieldSlider
          label={field.label}
          name={field.name}
          control={control}
          handleChange={(val) => {
            setValue(field.name, val);
          }}
          submit={(obj) => {
            handleSubmit(obj);
          }}
        />
      )}
      {field.type === 'tags' && (
        <ListTags
          name={field.name}
          tags={field.options}
          isMulti={field.isMulti}
          control={control}
          rules={rules}
        />
      )}
      {(field.type === 'pages' && pageId && watch) && (
        <ChildPagesField
          label={field.label}
          type={field.page}
          create={field.create || 'Ajouter'}
          parent={pageId}
          pages={watch('pages')?.filter((p : IPage) => p.type === field.page) || []}
        />
      )}
      {(field.type === 'select' && field?.options?.length > 0) && (
        <StyledSelect
          label={field.label}
          name={field.name}
          isMulti={field.isMulti}
          options={field.options}
          control={control}
          rules={rules}
        />
      )}
      {field.type === 'array' && watch && setIsSaved
        && <>
          <ObjectArray
            label={field.label}
            create={field.create || 'Ajouter'}
            blocks={field.blocks}
            array={watch(field.name) || []}
            handleChangeArray={(val) => {
              setValue(field.name, [...val]);
              setIsSaved(false);
            }}
            name={field.name}
            item={field.item}
            className={field?.className}
            fields={field.fields}
            handleSubmit={(obj) => handleSubmit(obj)}
          />
        </>
      }
      {getError() && <ErrorField message={getError()}/>}
    </div>
  );
};

export default Field;
